import { useEffect, useRef, useState } from 'react';

import { normalizeInlineEditValue } from './inlineEditInput/inlineEditInput.functions.ts';
import { StyledInlineEditInput } from './inlineEditInput/inlineEditInput.styles.ts';

export type InlineEditInputProps = {
  /** Current value; the draft starts from it with the text selected. */
  value: string;
  'aria-label': string;
  /**
   * Called with the trimmed value when it changed. Resolve `true` to finish; resolve `false` or
   * reject to keep the input open and focused (report the error from here).
   */
  onSave: (value: string) => Promise<boolean>;
  /**
   * The edit is over: saved, unchanged, or cancelled. `refocus` is true when it ended from the
   * keyboard (Enter / Escape), so the caller can hand focus back to whatever opened the input.
   */
  onDone: (args: { refocus: boolean }) => void;
  /** Leaving the field saves. Off by default, where leaving the field keeps the input open. */
  saveOnBlur?: boolean;
};

/**
 * Text input for renaming in place. Mount it while editing and unmount it on `onDone`; the caller
 * owns how editing starts and what saving does. Enter saves, Escape cancels, and an empty value is
 * flagged instead of saved.
 *
 * Key presses and clicks stop here, so a surrounding list's keyboard navigation or row click does
 * not act on them.
 */
export const InlineEditInput = ({
  value,
  'aria-label': ariaLabel,
  onSave,
  onDone,
  saveOnBlur = false,
}: InlineEditInputProps) => {
  const [draft, setDraft] = useState(value);
  const [invalid, setInvalid] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  /** Refs rather than state, so Enter followed by the blur it can cause does not save twice. */
  const savingRef = useRef(false);
  const finishedRef = useRef(false);

  useEffect(() => {
    inputRef.current?.select();
  }, []);

  const finish = (refocus: boolean) => {
    finishedRef.current = true;
    onDone({ refocus });
  };

  const save = async (refocus: boolean) => {
    if (finishedRef.current || savingRef.current) return;
    const normalized = normalizeInlineEditValue({ draft, currentValue: value });
    if (normalized.status === 'invalid') {
      setInvalid(true);
      inputRef.current?.focus();
      return;
    }
    if (normalized.status === 'unchanged') {
      finish(refocus);
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      if (await onSave(normalized.value)) {
        finish(refocus);
        return;
      }
    } catch {
      // The caller reports the failure; keep the draft so the user can retry.
    }
    savingRef.current = false;
    setSaving(false);
    inputRef.current?.focus();
  };

  return (
    <StyledInlineEditInput
      ref={inputRef}
      value={draft}
      readOnly={saving}
      aria-label={ariaLabel}
      aria-invalid={invalid}
      onChange={event => {
        setDraft(event.target.value);
        setInvalid(false);
      }}
      onBlur={saveOnBlur ? () => void save(false) : undefined}
      onClick={event => event.stopPropagation()}
      onKeyDown={event => {
        event.stopPropagation();
        if (event.key === 'Enter') {
          event.preventDefault();
          void save(true);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          finish(true);
        }
      }}
    />
  );
};
